import { useEffect, useMemo } from 'react';
import { skipToken, useQuery } from '@apollo/client/react';

import {
    IUserListItemFragment,
    IUserSelectorInput,
    IUserSelectorQueryVariables,
    AuthorsDocument,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { IContentQueryVariables } from 'common/types';

export interface IUserList {
    authors: IUserListItemFragment[];
    alreadyAddedAuthors: string[];
    authorsCount?: number | null;
    loading: boolean;
}

export const useAuthorList = (
    queryVariables?: IUserSelectorQueryVariables,
    contentQueryVariables?: IContentQueryVariables,
    selectorQueryVariables?: IUserSelectorInput
): IUserList => {
    const { showApolloError } = useApolloError();

    const { loading, data, error } = useQuery(
        AuthorsDocument,
        queryVariables
            ? {
                  variables: {
                      q: queryVariables.q,
                      offset: queryVariables.offset,
                      ...queryVariables,
                      ...contentQueryVariables,
                      selector: selectorQueryVariables,
                  },
              }
            : skipToken
    );

    useEffect(() => {
        if (error) {
            showApolloError(error);
        }
    }, [error, showApolloError]);

    const authorsObject = data?.authors;
    const authorsCount = authorsObject?.count;
    const authors = useMemo(
        () =>
            (authorsObject?.edges
                .map((edge) => edge?.node || null)
                .filter(Boolean) as IUserListItemFragment[]) || [],
        [authorsObject?.edges]
    );
    const alreadyAddedAuthors = useMemo(
        () =>
            authorsObject?.edges
                .map((edge) => {
                    if (edge?.alreadyAddedToObject) return edge.node.id;

                    return null;
                })
                .filter(Boolean) || [],
        [authorsObject?.edges]
    );

    return {
        authors,
        alreadyAddedAuthors,
        authorsCount,
        loading,
    };
};
